import * as React from 'react';
import { Phone } from 'lucide-react';
import { cn } from '@finmin/ui';

interface PhoneFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
  /** Country code shown as a non-editable prefix. Default: "+91" */
  countryCode?: string;
}

/**
 * Smart phone-number input.
 *
 * - Shows "+91" (or given countryCode) as a fixed visual prefix — user never
 *   types the country code; normalizePhone() adds it on submit.
 * - Accepts digits only; silently drops any other character.
 * - Auto-formats as "XXXXX XXXXX" (space after 5th digit) for readability.
 * - Caps input at 10 digits (Indian mobile numbers).
 * - The RHF field value is the raw digit string e.g. "9876543210" — no spaces,
 *   no country code. normalizePhone converts it to "+919876543210" on submit.
 */
export const PhoneField = React.forwardRef<HTMLInputElement, PhoneFieldProps>(
  (
    {
      value,
      onChange,
      hasError = false,
      countryCode = '+91',
      disabled,
      className,
      onBlur,
      name,
      id,
    },
    ref,
  ) => {
    /** Display value: insert a space after 5th digit */
    const displayValue = formatDisplay(value);

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      // Strip everything that isn't a digit
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      onChange(digits);
    }

    return (
      <div
        className={cn(
          'flex items-center rounded-md border bg-background ring-offset-background transition-all duration-150',
          hasError
            ? 'border-destructive focus-within:ring-2 focus-within:ring-destructive/30 focus-within:ring-offset-2'
            : 'border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        {/* Fixed country-code prefix — not editable */}
        <span className="flex items-center gap-1.5 pl-3 pr-2.5 border-r border-input text-sm font-medium text-muted-foreground select-none shrink-0">
          <Phone className="h-3.5 w-3.5" aria-hidden />
          {countryCode}
        </span>

        {/* Digit-only input */}
        <input
          ref={ref}
          id={id}
          name={name}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={displayValue}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="98765 43210"
          aria-label="Mobile number"
          className={cn(
            'flex-1 h-10 px-3 bg-transparent text-sm text-foreground',
            'placeholder:text-muted-foreground focus:outline-none',
            'disabled:cursor-not-allowed',
          )}
        />
      </div>
    );
  },
);
PhoneField.displayName = 'PhoneField';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** "9876543210" → "98765 43210" (space after 5th digit) */
function formatDisplay(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)} ${d.slice(5)}`;
}
